import type {
  ClientLocationOptionCatalogHistoryFilters,
  ClientOptionCatalogHistoryFilters,
  ClientOptionChangeEvent,
} from "api-types/optionCatalogs/optionCatalogHistoryResponses";
import { offsetDate, todayDate } from "common/utils/dates";
import SectionHeader from "lib/components/builders/communities/plans/SectionHeader";
import { useFetch, usePaginatedFetch } from "lib/fetcher";
import { useMemo, useState } from "react";
import Loading from "ui/components/Loading";
import InputGroupDateRange from "ui/forms/InputGroupDateRange";
import Select from "ui/forms/Select";
import type { OptionProps, SelectPropsOnChangeFunc } from "ui/forms/Select/Select";
import EmptySearchResults from "ui/search/EmptySearchResults";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import styles from "./optionHistory.module.scss";

export interface OptionHistoryFilterValues {
  startDate?: string;
  endDate?: string;
  planId?: string;
  optionSetId?: string;
  changeType?: string;
}

type OptionHistoryFilterOptions = ClientOptionCatalogHistoryFilters &
  Partial<Pick<ClientLocationOptionCatalogHistoryFilters, "plans">>;

interface OptionHistoryTabProps {
  changeTypeOptions: OptionProps[];
  columns: Column<ClientOptionChangeEvent>[];
  defaultRangeDays: number;
  filtersUrl: string | null;
  /** Places wider filter sets below the fixed-height header slot. */
  filtersPlacement: "headerSearch" | "belowHeader";
  historyUrlFor: (filters: OptionHistoryFilterValues) => string | null;
  includePlanFilter?: boolean;
  pageSize: number;
}

export default function OptionHistoryTab({
  changeTypeOptions,
  columns,
  defaultRangeDays,
  filtersUrl,
  filtersPlacement,
  historyUrlFor,
  includePlanFilter = false,
  pageSize,
}: OptionHistoryTabProps) {
  const [planFilter, setPlanFilter] = useState<string | null>(null);
  const [optionSetFilter, setOptionSetFilter] = useState<string | null>(null);
  const [changeTypeFilter, setChangeTypeFilter] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(() => offsetDate(-defaultRangeDays));
  const [endDate, setEndDate] = useState<string>(todayDate);

  // Date strings are parsed in local time (T00:00:00 / T23:59:59) then converted to UTC via
  // toISOString(), so the server receives UTC boundaries that match the user's local day.
  const filters = useMemo(() => {
    const nextFilters: OptionHistoryFilterValues = {};
    if (startDate) {
      nextFilters.startDate = new Date(`${startDate}T00:00:00`).toISOString();
    }
    if (endDate) {
      nextFilters.endDate = new Date(`${endDate}T23:59:59`).toISOString();
    }
    if (planFilter) {
      nextFilters.planId = planFilter;
    }
    if (optionSetFilter) {
      nextFilters.optionSetId = optionSetFilter;
    }
    if (changeTypeFilter) {
      nextFilters.changeType = changeTypeFilter;
    }
    return nextFilters;
  }, [changeTypeFilter, endDate, optionSetFilter, planFilter, startDate]);

  const historyUrl = historyUrlFor(filters);

  // A falsy dependency skips the request, so depend on the resolved URLs.
  const { data: filterOptions } = useFetch<OptionHistoryFilterOptions>(filtersUrl ?? "", [filtersUrl]);
  const {
    data: historyResponse,
    loading,
    pagination,
    setPagination,
  } = usePaginatedFetch<ClientOptionChangeEvent>(historyUrl ?? "", [historyUrl], {
    pageSize,
    paginationMethod: "offset",
  });

  const planOptions: OptionProps[] = useMemo(
    () => filterOptions?.plans?.map((plan) => ({ value: plan.id, label: plan.name })) ?? [],
    [filterOptions],
  );
  const optionSetOptions: OptionProps[] = useMemo(
    () => filterOptions?.optionSets.map((optionSet) => ({ value: optionSet.id, label: optionSet.name })) ?? [],
    [filterOptions],
  );

  if (!historyUrl || !filtersUrl) {
    return null;
  }

  const resetToFirstPage = () => setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  const selectFilter =
    (setValue: (next: string | null) => void): SelectPropsOnChangeFunc =>
    (val) => {
      setValue(val === "" || val === null ? null : String(val));
      resetToFirstPage();
    };
  const noResults = !loading && historyResponse && historyResponse.data.length === 0;
  const showChangeTypeFilter = changeTypeOptions.length > 1;

  const filterControls = (
    <div className={filtersPlacement === "headerSearch" ? styles.filterRow : styles.filterToolbar}>
      <InputGroupDateRange
        startDate={startDate}
        endDate={endDate}
        onStartChange={(val) => {
          setStartDate(val);
          resetToFirstPage();
        }}
        onEndChange={(val) => {
          setEndDate(val);
          resetToFirstPage();
        }}
        presets={["last7", "last30", "last90", "thisMonth", "thisYear"]}
        inputSize="medium"
      />
      {includePlanFilter && (
        <div className={styles.filterSelect}>
          <Select
            options={planOptions}
            value={planFilter ?? ""}
            onChange={selectFilter(setPlanFilter)}
            placeholder="Plan"
            selectSize="medium"
            clearable
          />
        </div>
      )}
      <div className={styles.filterSelect}>
        <Select
          options={optionSetOptions}
          value={optionSetFilter ?? ""}
          onChange={selectFilter(setOptionSetFilter)}
          placeholder="Option Set"
          selectSize="medium"
          clearable
        />
      </div>
      {showChangeTypeFilter && (
        <div className={styles.filterSelect}>
          <Select
            options={changeTypeOptions}
            value={changeTypeFilter ?? ""}
            onChange={selectFilter(setChangeTypeFilter)}
            placeholder="Change Type"
            selectSize="medium"
            clearable
          />
        </div>
      )}
    </div>
  );

  return (
    <div>
      <SectionHeader
        title="Option Changes"
        headerSearch={filtersPlacement === "headerSearch" ? filterControls : undefined}
        hr
      />
      {filtersPlacement === "belowHeader" && filterControls}
      {loading && <Loading variant="icon" />}
      {historyResponse && historyResponse.data.length > 0 && (
        <ConfigTable
          className={styles.compactTable}
          columns={columns}
          data={historyResponse.data}
          pagination={pagination}
          setPagination={setPagination}
          totalCount={historyResponse.totalCount}
        />
      )}
      {noResults && <EmptySearchResults />}
    </div>
  );
}
